import { LockOutlined } from "@ant-design/icons";
import { useMutation } from "@tanstack/react-query";
import { Alert, Form, Input, Modal } from "antd";

import { changePassword } from "../../api/auth";
import { ApiError } from "../../api/client";
import { useAuth } from "./AuthContext";

interface Values {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export function ChangePasswordModal({
  open,
  required,
  onClose,
}: {
  open: boolean;
  required: boolean;
  onClose: () => void;
}) {
  const [form] = Form.useForm<Values>();
  const auth = useAuth();
  const mutation = useMutation({
    mutationFn: (values: Values) => changePassword(values.currentPassword, values.newPassword),
    onSuccess: async () => {
      form.resetFields();
      await auth.refresh();
      onClose();
    },
  });

  return (
    <Modal
      title={required ? "首次登录，请修改初始密码" : "修改登录密码"}
      open={open}
      closable={!required}
      mask={{ closable: !required }}
      keyboard={!required}
      okText="确认修改"
      cancelText="取消"
      cancelButtonProps={{ style: { display: required ? "none" : undefined } }}
      confirmLoading={mutation.isPending}
      onCancel={onClose}
      onOk={() => form.submit()}
      destroyOnHidden
    >
      {mutation.isError ? (
        <Alert
          className="form-alert"
          type="error"
          showIcon
          title={mutation.error instanceof ApiError ? mutation.error.message : "密码修改失败"}
        />
      ) : null}
      <Form<Values>
        form={form}
        layout="vertical"
        requiredMark={false}
        onFinish={(values) => mutation.mutate(values)}
      >
        <Form.Item
          name="currentPassword"
          label="当前密码"
          rules={[{ required: true, message: "请输入当前密码" }]}
        >
          <Input.Password prefix={<LockOutlined />} autoComplete="current-password" />
        </Form.Item>
        <Form.Item
          name="newPassword"
          label="新密码"
          extra="12–128 位，需包含大小写字母、数字、符号中的至少三类"
          rules={[{ required: true, min: 12, message: "新密码至少 12 位" }]}
        >
          <Input.Password prefix={<LockOutlined />} autoComplete="new-password" />
        </Form.Item>
        <Form.Item
          name="confirmPassword"
          label="确认新密码"
          dependencies={["newPassword"]}
          rules={[
            { required: true, message: "请再次输入新密码" },
            ({ getFieldValue }) => ({
              validator(_, value) {
                return !value || getFieldValue("newPassword") === value
                  ? Promise.resolve()
                  : Promise.reject(new Error("两次输入的密码不一致"));
              },
            }),
          ]}
        >
          <Input.Password prefix={<LockOutlined />} autoComplete="new-password" />
        </Form.Item>
      </Form>
    </Modal>
  );
}
